// JOBE-0164.4: AgentRankingTable — sortable, dense per-agent ranking
// for the W6 Fleet surface. Default sort: pass rate ascending
// (worst at top — operators look at problems first).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { withDrillContext } from '../lib/drillContext.js';
import './AgentRankingTable.css';

const COLUMNS = [
  { key: 'rank',         label: '#',                      sortable: false },
  { key: 'agent',        label: 'Agent',                  sortable: true  },
  { key: 'prompt',       label: 'Prompt',                 sortable: true  },
  { key: 'passRate',     label: 'Eval pass rate · 30d',   sortable: true  },
  { key: 'delta',        label: 'Δ',                      sortable: true  },
  { key: 'failureCount', label: 'Corr.',                  sortable: true  },
  { key: 'open',         label: 'Open',                   sortable: false },
];

function titleCase(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function passRateTone(rate) {
  if (rate >= 0.90) return 'ok';
  if (rate >= 0.80) return '';
  if (rate >= 0.70) return 'warn';
  return 'bad';
}

function fmtDelta(delta) {
  if (delta == null || delta === 0) return { text: 'flat', cls: '' };
  // Express in whole points (×100 of the rate); 0.02 → 2.
  const pts = Math.round(delta * 100);
  if (pts > 0) return { text: `↑ +${pts} pt`, cls: 'up' };
  return { text: `↓ −${Math.abs(pts)} pt`, cls: 'dn' };
}

function initialsOf(name, displayName) {
  const src = (displayName || name || '').trim();
  if (!src) return '?';
  return src.charAt(0).toUpperCase();
}

function comparatorFor(col) {
  switch (col) {
    case 'agent':       return (a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name);
    case 'prompt':      return (a, b) => (a.promptVersion ?? 0) - (b.promptVersion ?? 0);
    case 'passRate':    return (a, b) => (a.evalPassRate ?? 0) - (b.evalPassRate ?? 0);
    case 'delta':       return (a, b) => (a.evalDelta ?? 0) - (b.evalDelta ?? 0);
    case 'failureCount':return (a, b) => (a.failureCount ?? 0) - (b.failureCount ?? 0);
    default:            return () => 0;
  }
}

/**
 * @param {object} props
 * @param {object} props.fleet  loaded `promptops.mock.fleet` fixture
 */
export function AgentRankingTable({ fleet }) {
  const navigate = useNavigate();
  // Default sort: passRate ascending (worst at top).
  const [sort, setSort] = useState({ column: 'passRate', dir: 'asc' });

  const rows = useMemo(() => {
    const raw = fleet?.agents ?? [];
    const cmp = comparatorFor(sort.column);
    const sorted = [...raw].sort(cmp);
    if (sort.dir === 'desc') sorted.reverse();
    return sorted.map((r, i) => ({ ...r, _rank: i + 1 }));
  }, [fleet, sort]);

  function toggleSort(col) {
    setSort((prev) => {
      if (prev.column !== col) return { column: col, dir: 'asc' };
      return { column: col, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  }

  function drillTo(row) {
    navigate(
      withDrillContext(`/promptops/agent/${row.name}`, {
        source: 'fleet-agent-ranking',
        rank: row._rank,
        sortColumn: sort.column,
        sortDir: sort.dir,
      })
    );
  }

  if (rows.length === 0) {
    return (
      <section id="agent-ranking" className="agent-ranking" aria-label="Agent ranking">
        <p className="ranking-empty">No agents in the fleet — add one in Organization.</p>
      </section>
    );
  }

  return (
    <section id="agent-ranking" className="agent-ranking" aria-label="Agent ranking">
      <header className="agent-ranking__head">
        <h2 className="agent-ranking__title">Agent ranking</h2>
        <span className="agent-ranking__caption">click any row → W5 agent</span>
      </header>
      <table className="agent-ranking__table" role="table">
        <thead>
          <tr className="agent-ranking__hdr">
            {COLUMNS.map((c) => {
              const isActive = sort.column === c.key;
              const arrow = isActive ? (sort.dir === 'asc' ? '▲' : '▼') : '';
              return (
                <th
                  key={c.key}
                  className={`agent-ranking__th agent-ranking__th--${c.key}${c.sortable ? ' is-sortable' : ''}${isActive ? ' is-active' : ''}`}
                  onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                  scope="col"
                >
                  {c.label}{arrow ? ` ${arrow}` : ''}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const tone = passRateTone(r.evalPassRate ?? 0);
            const pct = Math.round((r.evalPassRate ?? 0) * 100);
            const barWidth = Math.round((r.passRateBar ?? r.evalPassRate ?? 0) * 100);
            const delta = fmtDelta(r.evalDelta);
            return (
              <tr
                key={r.name}
                className="agent-ranking__row"
                onClick={() => drillTo(r)}
                role="row"
              >
                <td className="agent-ranking__td agent-ranking__td--rank">{r._rank}</td>
                <td className="agent-ranking__td agent-ranking__td--agent">
                  <span className="agent-avatar">{initialsOf(r.name, r.displayName)}</span>
                  <span className="agent-name">{r.displayName || titleCase(r.name)}</span>
                  <span className="agent-meta">{r.dept || ''}{r.role ? ` · ${r.role}` : ''}</span>
                </td>
                <td className="agent-ranking__td agent-ranking__td--prompt">
                  <span className="prompt-curr">{r.name}@{r.promptVersion ?? '—'}</span>
                </td>
                <td className="agent-ranking__td agent-ranking__td--passRate">
                  <div className="bar-wrap">
                    <span className={`v ${tone}`}>{pct}%</span>
                    <div className="bar">
                      <i className={tone} style={{ width: `${barWidth}%` }} />
                    </div>
                  </div>
                </td>
                <td className={`agent-ranking__td agent-ranking__td--delta ${delta.cls}`}>
                  {delta.text}
                </td>
                <td className="agent-ranking__td agent-ranking__td--failureCount">{r.failureCount ?? 0}</td>
                <td className="agent-ranking__td agent-ranking__td--open">
                  <button
                    type="button"
                    className="agent-ranking__open-btn"
                    onClick={(e) => { e.stopPropagation(); drillTo(r); }}
                  >
                    → W5
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

export default AgentRankingTable;
